'use client'

import { useEffect, useRef, useState } from 'react'
import { FaPause, FaPlay } from 'react-icons/fa'
import { useReducedMotion } from 'src/components/site/use-media-query'

const ASSETS = { background: '/images/background.jpg' }
const MOTION = { cycleSeconds: 14, amplitude: 1.6, parallax: 0.8 }

export const WaveBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const artRef = useRef<HTMLDivElement>(null)
  const pausedRef = useRef(false)
  const goRef = useRef<() => void>(() => {})
  const [paused, setPaused] = useState(false)
  const reduced = useReducedMotion()

  useEffect(() => {
    const cv = canvasRef.current
    const art = artRef.current
    if (!cv || !art) return
    const x = cv.getContext('2d')
    if (!x) return
    const rm = matchMedia('(prefers-reduced-motion: reduce)')
    const M = MOTION

    let W = 0
    let H = 0
    let mob = false
    let docH = document.documentElement.scrollHeight
    let t = 0
    let last = 0
    let raf = 0

    function draw() {
      if (!x) return
      x.clearRect(0, 0, W, H)
      const sy = scrollY * M.parallax
      const n = mob ? 12 : 26
      const st = mob ? 14 : 8
      const gap = mob ? 7 : 6
      const sp = mob ? 800 : 780
      const w = (Math.PI * 2) / M.cycleSeconds
      const A = M.amplitude
      for (let r = 0; r < Math.ceil(docH / sp) + 1; r++) {
        const side = r % 2 ? 1 : -1
        const base = r * sp + 240 - sy
        if (base < -420 || base > H + 420) continue
        const amp = (mob ? 40 : 70 + ((r * 37) % 40)) * A
        const x0 = side > 0 ? W * 0.12 : 0
        const x1 = side > 0 ? W : W * 0.88
        const g = x.createLinearGradient(side > 0 ? x0 : x1, 0, side > 0 ? x1 : x0, 0)
        g.addColorStop(0, 'rgba(121,233,250,0)')
        g.addColorStop(1, 'rgba(121,233,250,1)')
        x.strokeStyle = g
        x.lineWidth = mob ? 0.9 : 1
        for (let i = 0; i < n; i++) {
          x.globalAlpha = 0.18 + 0.6 * (i / n) * (0.65 + 0.35 * Math.sin(i * 0.7 + r + t * w))
          x.beginPath()
          for (let px = x0; px <= x1 + st; px += st) {
            const u = px / W
            const e = side > 0 ? u : 1 - u
            const y =
              base +
              38 * A * Math.sin(t * w * 0.5 + r * 1.3) +
              i * gap * (1 + 0.35 * Math.sin(t * w + u * 2 + r)) -
              e * e * (mob ? 150 : 260) +
              amp * Math.sin(u * 3.2 * Math.PI * 0.6 + t * w + r) +
              26 * A * Math.sin(u * 9 - t * w * 2 + i * 0.06 + r) +
              14 * A * Math.sin(u * 17 + t * w * 3 - i * 0.1)
            if (px === x0) x.moveTo(px, y)
            else x.lineTo(px, y)
          }
          x.stroke()
        }
      }
      x.globalAlpha = 1
      if (!rm.matches) art!.style.transform = `translate3d(0,${-scrollY * 0.06}px,0)`
    }

    function size() {
      if (!cv || !x) return
      mob = innerWidth < 720
      const d = Math.min(devicePixelRatio || 1, mob ? 1.5 : 2)
      W = innerWidth
      H = innerHeight
      cv.width = W * d
      cv.height = H * d
      x.setTransform(d, 0, 0, d, 0, 0)
      docH = document.documentElement.scrollHeight
      draw()
    }

    function loop(ts: number) {
      raf = 0
      if (pausedRef.current || document.hidden || rm.matches) return
      if (!mob || ts - last > 33) {
        t += Math.min((ts - last) / 1000, 0.1)
        last = ts
        draw()
      }
      raf = requestAnimationFrame(loop)
    }

    function go() {
      if (!raf && !pausedRef.current && !document.hidden && !rm.matches) {
        last = performance.now()
        raf = requestAnimationFrame(loop)
      }
    }
    goRef.current = go

    const onScroll = () => {
      if (!raf) requestAnimationFrame(draw)
    }
    const onRm = () => {
      draw()
      go()
    }
    const ro = new ResizeObserver(() => {
      docH = document.documentElement.scrollHeight
    })

    addEventListener('resize', size)
    addEventListener('scroll', onScroll, { passive: true })
    document.addEventListener('visibilitychange', go)
    rm.addEventListener('change', onRm)
    ro.observe(document.body)
    size()
    go()

    return () => {
      cancelAnimationFrame(raf)
      removeEventListener('resize', size)
      removeEventListener('scroll', onScroll)
      document.removeEventListener('visibilitychange', go)
      rm.removeEventListener('change', onRm)
      ro.disconnect()
    }
  }, [])

  const label = paused ? 'Resume background motion' : 'Pause background motion'

  return (
    <>
      <div
        ref={artRef}
        className="art"
        aria-hidden="true"
        style={{ backgroundImage: `url(${ASSETS.background})` }}
      />
      <canvas ref={canvasRef} id="waves" aria-hidden="true" />
      {!reduced && (
        <button
          className="pause"
          type="button"
          aria-pressed={paused}
          aria-label={label}
          title={label}
          onClick={() => {
            pausedRef.current = !pausedRef.current
            setPaused(pausedRef.current)
            goRef.current()
          }}
        >
          {paused ? (
            <FaPlay aria-hidden="true" />
          ) : (
            <FaPause aria-hidden="true" />
          )}
        </button>
      )}
    </>
  )
}
